import React, { useMemo } from 'react'
import { Box, Tooltip, Typography, Stack } from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import InsightsIcon from '@mui/icons-material/Insights'
import { formatDisplayName } from '../../../../utils/display-name-formatter'

interface ThresholdData {
  threshold_name: string
  lr_pos_index: number
  lr_neg_index: number
}

interface ConditionData {
  condition_name: string
  condition_display_name: string
  programs?: string[]
  thresholds?: ThresholdData[]
  sensitivity?: number
  specificity?: number
}

interface Props {
  condsPerTest: ConditionData[]
}

const TestDiagnosticImpactIndicator: React.FC<Props> = ({ condsPerTest }) => {
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)

  const patientPrograms = useMemo(() => {
    return patientDetail?.program_enrollments?.map((e: any) => e.program) || []
  }, [patientDetail?.program_enrollments])

  // Calculate the most extreme PLR and NLR across all conditions/thresholds
  // Also separate into program-matched and non-program conditions
  const { programConditions, nonProgramConditions, maxPlr, minNlr } = useMemo(() => {
    if (!condsPerTest?.length) return { programConditions: [], nonProgramConditions: [], maxPlr: 0, minNlr: 0 }

    const programMatched: ConditionData[] = []
    const nonProgram: ConditionData[] = []
    let maxPlr = 0
    let minNlr = 0

    condsPerTest.forEach(c => {
      const isProgramMatch = c.programs?.some(p => patientPrograms.includes(p)) || false

      if (isProgramMatch) {
        programMatched.push(c)
      } else {
        nonProgram.push(c)
      }

      // Find max PLR and min NLR (most negative) from thresholds
      c.thresholds?.forEach(t => {
        if (t.lr_pos_index > maxPlr) maxPlr = t.lr_pos_index
        if (t.lr_neg_index < minNlr) minNlr = t.lr_neg_index
      })
    })

    // Sort each group alphabetically
    const sortFn = (a: ConditionData, b: ConditionData) =>
      (a.condition_display_name || a.condition_name || '').localeCompare(
        b.condition_display_name || b.condition_name || ''
      )
    programMatched.sort(sortFn)
    nonProgram.sort(sortFn)

    return {
      programConditions: programMatched,
      nonProgramConditions: nonProgram,
      maxPlr,
      minNlr
    }
  }, [condsPerTest, patientPrograms])

  const programCount = programConditions.length
  const nonProgramCount = nonProgramConditions.length
  const totalCount = programCount + nonProgramCount

  if (!condsPerTest?.length) return null

  // Render PLR bar (green) - scale: 0-2, values >= 2 show as full bar
  const renderPlrBar = (plr: number) => {
    const width = Math.min(Math.max((plr / 2) * 100, 0), 100)
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" sx={{ fontSize: '0.55rem', width: '22px', color: 'var(--color-text-secondary)' }}>
          PLR
        </Typography>
        <Box sx={{ width: 50, height: 5, backgroundColor: 'var(--color-border-medium)', borderRadius: 1 }}>
          <Box
            sx={{
              width: `${width}%`,
              height: '100%',
              backgroundColor: '#4caf50',
              borderRadius: 1,
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ fontSize: '0.55rem', minWidth: '24px', color: 'var(--color-text-secondary)' }}>
          {plr.toFixed(1)}
        </Typography>
      </Stack>
    )
  }

  // Render NLR bar (blue) - scale: 0 to -2, values <= -2 show as full bar
  const renderNlrBar = (nlr: number) => {
    const width = Math.min(Math.max((Math.abs(nlr) / 2) * 100, 0), 100)
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" sx={{ fontSize: '0.55rem', width: '22px', color: 'var(--color-text-secondary)' }}>
          NLR
        </Typography>
        <Box sx={{ width: 50, height: 5, backgroundColor: 'var(--color-border-medium)', borderRadius: 1 }}>
          <Box
            sx={{
              width: `${width}%`,
              height: '100%',
              backgroundColor: '#2196f3',
              borderRadius: 1,
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ fontSize: '0.55rem', minWidth: '24px', color: 'var(--color-text-secondary)' }}>
          {nlr.toFixed(1)}
        </Typography>
      </Stack>
    )
  }

  const renderCondition = (condition: ConditionData, index: number) => {
    const isProgramMatch = condition.programs?.some(p => patientPrograms.includes(p)) || false
    const displayName = condition.condition_display_name || condition.condition_name || 'Unknown'

    return (
      <Box key={`condition-${index}`} sx={{ mb: 0.75 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {isProgramMatch && (
            <Typography sx={{ color: '#1976d2', fontSize: '0.65rem' }}>★</Typography>
          )}
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.65rem',
              fontWeight: isProgramMatch ? 600 : 400,
              color: isProgramMatch ? '#1976d2' : '#333',
            }}
          >
            {displayName}
          </Typography>
        </Stack>
        {/* Show each threshold with its evidence name and PLR/NLR */}
        {condition.thresholds?.map((threshold, tIndex) => (
          <Box key={`threshold-${tIndex}`} sx={{ pl: isProgramMatch ? 1.5 : 0, mt: 0.25 }}>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.55rem', color: 'var(--color-text-secondary)', display: 'block', mb: 0.25 }}
            >
              - {formatDisplayName(threshold.threshold_name)}
            </Typography>
            <Box sx={{ pl: 1 }}>
              {renderPlrBar(threshold.lr_pos_index || 0)}
              {renderNlrBar(threshold.lr_neg_index || 0)}
            </Box>
          </Box>
        ))}
        {/* Show sensitivity/specificity if no thresholds */}
        {!condition.thresholds?.length && (condition.sensitivity || condition.specificity) && (
          <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'var(--color-text-secondary)', pl: isProgramMatch ? 1.5 : 0 }}>
            Sensitivity: {condition.sensitivity?.toFixed(2) || '-'}, Specificity: {condition.specificity?.toFixed(2) || '-'}
          </Typography>
        )}
      </Box>
    )
  }

  const tooltipContent = (
    <Box sx={{ p: 0.5, maxWidth: 380, maxHeight: 400, overflowY: 'auto' }}>
      <Typography variant="subtitle2" sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>
        Conditions Affected by Test
      </Typography>
      {/* Program conditions section */}
      {programConditions.length > 0 && (
        <Box sx={{ borderTop: '1px solid #ddd', pt: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#1976d2', display: 'block', mb: 0.5 }}
          >
            Program Conditions ({programCount})
          </Typography>
          {programConditions.map((condition, index) => renderCondition(condition, index))}
        </Box>
      )}
      {/* Non-program conditions section */}
      {nonProgramConditions.length > 0 && (
        <Box sx={{ borderTop: '1px solid #ddd', pt: 0.5, mt: programConditions.length > 0 ? 0.5 : 0 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#757575', display: 'block', mb: 0.5 }}
          >
            Impacted Conditions ({nonProgramCount})
          </Typography>
          {nonProgramConditions.map((condition, index) => renderCondition(condition, index))}
        </Box>
      )}
    </Box>
  )

  // Render badge showing condition count and most extreme values
  const hasProgramMatch = programCount > 0

  return (
    <Tooltip
      title={tooltipContent}
      placement="right"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'var(--color-paper)',
            color: 'var(--color-text-primary)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '1px solid var(--color-border-light)',
            maxWidth: 400,
            '& .MuiTooltip-arrow': {
              color: 'var(--color-paper)',
              '&::before': {
                border: '1px solid var(--color-border-light)',
              },
            },
          },
        },
      }}
    >
      <Stack
        direction="row"
        spacing={0.25}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'pointer',
          ml: 0.5,
          px: 0.5,
          py: 0.1,
          borderRadius: 1,
          backgroundColor: hasProgramMatch ? '#e3f2fd' : '#f5f5f5',
          '&:hover': {
            backgroundColor: hasProgramMatch ? '#bbdefb' : '#eeeeee',
          },
        }}
      >
        <InsightsIcon
          sx={{
            fontSize: '0.7rem',
            color: hasProgramMatch ? '#1976d2' : '#757575',
            mr: 0.25,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.55rem',
            fontWeight: 500,
            color: hasProgramMatch ? '#1976d2' : '#757575',
          }}
        >
          {totalCount}
        </Typography>
      </Stack>
    </Tooltip>
  )
}

export default TestDiagnosticImpactIndicator
